import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBytes } from '@/entities/attachment';
import {
  type ImportPreview,
  type ImportResult,
  useImportBoard,
  useImportPreview,
} from '@/entities/board';
import { isApiError } from '@/shared/api';
import { useErrorMessage } from '@/shared/i18n';
import { Button, Dialog } from '@/shared/ui';

import styles from './Portability.module.css';

/** Must match the server's limit for one archive. */
export const MAX_IMPORT_BYTES = 300 * 1024 * 1024;

const REASONS = {
  ARCHIVE_UNSAFE_PATH: 'portability.reason.ARCHIVE_UNSAFE_PATH',
  ARCHIVE_CHECKSUM_MISMATCH: 'portability.reason.ARCHIVE_CHECKSUM_MISMATCH',
  ARCHIVE_MEDIA_REQUIRED: 'portability.reason.ARCHIVE_MEDIA_REQUIRED',
  ARCHIVE_BROKEN_REFERENCE: 'portability.reason.ARCHIVE_BROKEN_REFERENCE',
  ARCHIVE_DUPLICATE_REF: 'portability.reason.ARCHIVE_DUPLICATE_REF',
  ARCHIVE_INVALID_RECURRENCE: 'portability.reason.ARCHIVE_INVALID_RECURRENCE',
  ARCHIVE_INVALID: 'portability.reason.ARCHIVE_INVALID',
  ARCHIVE_TOO_LARGE: 'portability.reason.ARCHIVE_TOO_LARGE',
  ARCHIVE_VERSION_UNSUPPORTED: 'portability.reason.ARCHIVE_VERSION_UNSUPPORTED',
} as const;

const WARNINGS = {
  RECURRENCE_PAUSED: 'portability.import.warning.RECURRENCE_PAUSED',
  LINK_PREVIEWS_REFETCHED: 'portability.import.warning.LINK_PREVIEWS_REFETCHED',
} as const;

type Phase = 'choose' | 'validating' | 'ready' | 'importing';

interface Props {
  workspaceId: string;
  onClose: () => void;
  onImported: (result: ImportResult) => void;
}

/**
 * Import a board archive in four visible steps: choose a file, validate it (a read-only preview),
 * confirm (name, what will happen), import. The request id is generated once per chosen file, so a
 * retry or a double click can never create two boards.
 */
export function ImportBoardDialog({ workspaceId, onClose, onImported }: Props) {
  const { t, i18n } = useTranslation();
  const getMessage = useErrorMessage();
  const preview = useImportPreview(workspaceId);
  const importBoard = useImportBoard(workspaceId);
  const input = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [requestId, setRequestId] = useState('');
  const [data, setData] = useState<ImportPreview | null>(null);
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<Phase>('choose');
  const [percent, setPercent] = useState(0);
  const [problem, setProblem] = useState<unknown>(null);

  const describe = (error: unknown): string => {
    const reason = isApiError(error) ? error.details?.['reason'] : undefined;
    if (typeof reason === 'string' && reason in REASONS) {
      return t(REASONS[reason as keyof typeof REASONS]);
    }
    return getMessage(error);
  };

  const choose = (selected: File | undefined) => {
    if (!selected) return;
    setProblem(null);
    setData(null);
    setFile(selected);
    setRequestId(crypto.randomUUID());
    if (selected.size > MAX_IMPORT_BYTES) {
      setPhase('choose');
      setProblem(
        new Error(
          t('portability.import.fileTooLarge', {
            limit: formatBytes(MAX_IMPORT_BYTES, i18n.language),
          }),
        ),
      );
      return;
    }
    setPhase('validating');
    abort.current = new AbortController();
    preview.mutate(
      { file: selected, signal: abort.current.signal },
      {
        onSuccess: (result) => {
          setData(result);
          setName(result.suggested_name);
          setPhase('ready');
        },
        onError: (error) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setProblem(error);
          setPhase('choose');
        },
      },
    );
  };

  const start = () => {
    if (!file || phase === 'importing' || name.trim() === '') return;
    setProblem(null);
    setPhase('importing');
    setPercent(0);
    abort.current = new AbortController();
    importBoard.mutate(
      {
        file,
        requestId,
        name: name.trim(),
        signal: abort.current.signal,
        onProgress: (loaded, total) => {
          setPercent(total > 0 ? Math.round((loaded / total) * 100) : 0);
        },
      },
      {
        onSuccess: (result) => {
          onImported(result);
        },
        onError: (error) => {
          if (error instanceof DOMException && error.name === 'AbortError') {
            setPhase('ready');
            return;
          }
          setProblem(error);
          setPhase('ready');
        },
      },
    );
  };

  const close = () => {
    abort.current?.abort();
    onClose();
  };

  const busy = phase === 'validating' || phase === 'importing';

  return (
    <Dialog title={t('portability.import.title')} onClose={close}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          start();
        }}
      >
        <p className={styles.intro}>{t('portability.import.intro')}</p>

        <input
          ref={input}
          type="file"
          className={styles.fileInput}
          accept=".zip,.json,application/zip,application/json"
          aria-label={t('portability.import.fileLabel')}
          tabIndex={-1}
          onChange={(event) => {
            choose(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <div className={styles.actions}>
          <Button
            disabled={busy}
            onClick={() => {
              input.current?.click();
            }}
          >
            {file ? t('portability.import.chooseAnother') : t('portability.import.choose')}
          </Button>
          {file && (
            <span className={styles.meta}>
              {t('portability.import.fileInfo', {
                name: file.name,
                size: formatBytes(file.size, i18n.language),
              })}
            </span>
          )}
        </div>

        {phase === 'validating' && (
          <p className={styles.note} role="status">
            {t('portability.import.validating')}
          </p>
        )}

        {problem !== null && (
          <p className={styles.error} role="alert">
            {problem instanceof Error && !isApiError(problem) ? problem.message : describe(problem)}
          </p>
        )}

        {data && (phase === 'ready' || phase === 'importing') && (
          <>
            <h3 className={styles.formatTitle}>{t('portability.import.previewTitle')}</h3>
            <ul className={styles.counts}>
              <li>{t('portability.import.version', { version: data.schema_version })}</li>
              <li>{t('portability.import.count.columns', { count: data.columns })}</li>
              <li>
                {t('portability.import.count.cards', { count: data.cards })}
                {data.archived_cards > 0 &&
                  ` · ${t('portability.import.count.archived', { count: data.archived_cards })}`}
              </li>
              <li>{t('portability.import.count.labels', { count: data.labels })}</li>
              <li>
                {t('portability.import.count.attachments', {
                  count: data.attachments,
                  size: formatBytes(data.media_bytes, i18n.language),
                })}
              </li>
              <li>{t('portability.import.count.series', { count: data.recurrence_series })}</li>
            </ul>
            {data.warnings
              .filter((code): code is keyof typeof WARNINGS => code in WARNINGS)
              .map((code) => (
                <p key={code} className={styles.warning} role="note">
                  {t(WARNINGS[code])}
                </p>
              ))}
            <div className={styles.field}>
              <label htmlFor="import-board-name">{t('portability.import.nameLabel')}</label>
              <input
                id="import-board-name"
                value={name}
                maxLength={100}
                disabled={phase === 'importing'}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </div>
            <p className={styles.note}>{t('portability.import.notice')}</p>
          </>
        )}

        {phase === 'importing' && (
          <div role="status">
            <p className={styles.note}>
              {percent < 100
                ? t('portability.import.uploading', { percent })
                : t('portability.import.importing')}
            </p>
            <progress className={styles.progress} max={100} value={percent} />
          </div>
        )}

        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            disabled={phase !== 'ready' || name.trim() === ''}
          >
            {problem !== null && phase === 'ready'
              ? t('portability.import.retry')
              : t('portability.import.confirm')}
          </Button>
          <Button onClick={close}>{t('portability.import.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
